import angularModule from 'Settings/angularModule/scripts/settings_module';
import template from 'Settings/angularModule/views/tuition_and_registration/cohort_registration_full_scholarship.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { TuitionPlanFrequency } from 'TuitionPlan';
import { selectablePaymentSituationForTuitionPlanFrequency } from 'PaymentSituation';
import { getAdmissionOffer } from 'Users';
import { formatCurrencyForDisplay } from 'Currency';
import {
    attachCohortDatesFn,
    attachCohortRegistrationOptionsFn,
    attachDeferralModalFn,
    attachRegisterButtonWatchFn,
    attachRegisterEnabledWatchFn,
} from './CohortRegistrationDirHelpers';
import { RegisterButtonState } from './TuitionAndRegistration.types';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('cohortRegistrationFullScholarship', [
    '$injector',

    function factory($injector) {
        const $rootScope = $injector.get('$rootScope');
        const ErrorLogService = $injector.get('ErrorLogService');
        const $http = $injector.get('$http');
        const $window = $injector.get('$window');

        return {
            restrict: 'E',
            templateUrl,
            scope: {
                // This is called from inside the smallprint locale
                launchProgramGuide: '&',
            },
            link(scope) {
                //-------------------------
                // Initialization
                //-------------------------
                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        return $rootScope.currentUser;
                    },
                });

                // A user should never see this page if their active AOI record isn't an AdmissionOffer.
                scope.admissionOffer = getAdmissionOffer(scope.currentUser);

                // While it is technically possible for an admission offer to have multiple cohort section offers
                // (in the sense that the data model supports it), it should not be possible for a user to be offered
                // admission with multiple cohort section offers going forward. We can safely look at the first element
                // in the array here since this UI only applies to folks who are offered admission.
                scope.cohortSectionOffer = scope.admissionOffer?.cohortSectionOffers[0];

                // It shouldn't be possible to get here without a CohortSectionOffer or an AdmissionOffer.
                // If we do, we want to know about it. If this error is logged, the UI is definitely broken for this user.
                if (!scope.cohortSectionOffer) {
                    ErrorLogService.notifyInProd(
                        'Trying to render cohortRegistrationFullScholarship UI without a CohortSectionOffer',
                        undefined,
                        {
                            userId: scope.currentUser.id,
                        },
                    );
                    $rootScope.goHome('cohort_registration_full_scholarship_dir::no_cohort_section_offer');
                }

                const fullScholarshipPaymentSituation = selectablePaymentSituationForTuitionPlanFrequency(
                    scope.cohortSectionOffer?.studentSelectablePaymentSituations,
                    TuitionPlanFrequency.FullScholarship,
                );

                scope.programInclusionProxy = {
                    shareable_with_classmates: true,
                };

                scope.totalScholarshipForDisplay = formatCurrencyForDisplay(
                    scope.cohortSectionOffer?.cumulativeOfferedScholarship?.amount,
                );

                //-------------------------
                // Registration helpers
                //-------------------------

                // We need to call these after initialization above because they use things on the scope.
                attachDeferralModalFn(scope, $injector, scope.cohortSectionOffer?.cumulativeOfferedScholarship);
                attachRegisterButtonWatchFn(scope, RegisterButtonState.register);
                attachRegisterEnabledWatchFn(scope);
                attachCohortDatesFn(scope);
                attachCohortRegistrationOptionsFn(scope);

                // If there are multiple Cohort options to register into, preselect the first.
                // It shouldn't be possible for scope.cohortRegistrationOptions to not have at least one option, for the offer Cohort,
                // but fallback to the offer Cohort if that's somehow happened.
                scope.registerForCohortIdProxy = {
                    value: scope.cohortRegistrationOptions[0]?.cohortId || scope.admissionOffer?.cohortId,
                };

                //-------------------------
                // Locale Keys
                //-------------------------

                scope.narrativeKey = 'settings.tuition_and_registration.narrative_full_scholarship';
                scope.graphicTitleKey = 'settings.tuition_and_registration.graphic_full_scholarship_title';
                scope.graphicSubtitleKey = 'settings.tuition_and_registration.graphic_full_scholarship_subtitle';

                scope.smallPrintKey = () => {
                    let key = 'smallprint_scholarship';

                    // Extract cohort number from cohort name (e.g., "MBA74" -> "74")
                    const cohortName = scope.currentUser.relevantCohort?.name;
                    const cohortNumberMatch = cohortName?.match(/(\d+)$/);
                    const cohortNumber = cohortNumberMatch ? cohortNumberMatch[1] : null;

                    // Add cohort-specific suffix for cohorts 74 and 75
                    if (cohortNumber === '74' || cohortNumber === '75') {
                        key += `_cohort_${cohortNumber}`;
                    }

                    if (!scope.reapplicationDate) {
                        key += '_no_reapplication_date';
                    }

                    return `settings.tuition_and_registration.${key}`;
                };

                scope.tuitionDetailsLabelKey = 'settings.tuition_and_registration.tuition_and_scholarship_details';

                //-------------------------
                // SelfRegister UMA handling
                //-------------------------

                scope.handleRegisterButtonClick = () => {
                    scope.processingRegistration = true;
                    $http
                        .post(`${$window.ENDPOINT_ROOT}/api/user_actions/admission_offer/self_register.json`, {
                            admission_offer_id: scope.admissionOffer?.id,
                            cohort_id: scope.registerForCohortIdProxy.value,
                            shareable_with_classmates: scope.programInclusionProxy.shareable_with_classmates,
                            tuition_plan_id: fullScholarshipPaymentSituation.tuitionPlan.id,
                        })
                        .then(() => {
                            scope.processingRegistration = false;
                        });
                };
            },
        };
    },
]);
